<script setup lang="ts">
/**
 * Paints this client's brand onto the running app.
 *
 * `tokens.css` ships Altamont's own navy and orange as the defaults. A client who has chosen
 * their own two colours in Settings gets them here instead: the pair is expanded by
 * `brandPalette` — the one place in the codebase where colour maths lives — and the result is
 * written as inline custom properties on `<html>`, which beat the `:root` rule in the stylesheet
 * without a single selector being touched. Every screen, button and chip follows, because they
 * were all built from the tokens rather than from hex values.
 *
 * Three rules this component exists to keep:
 *
 *  - **No maths here.** The hover shade and, more importantly, the *text* that sits on each
 *    ground are computed in `@altamont/shared` and checked for contrast there. A hard-coded
 *    foreground is exactly how a client who picks pale yellow ends up with white labels nobody
 *    can read, so this component never picks one.
 *  - **Nothing until the config has arrived.** Applying a half-known palette would repaint the
 *    page under someone mid-read. Until `GET /api/config` has answered, the stylesheet defaults
 *    stand on their own, which is why they are a complete, accessible palette and not placeholders.
 *  - **Put it back on the way out.** Unmounting removes only the properties this component set,
 *    so the defaults in `tokens.css` return and nothing is left behind for the next mount.
 *
 * It renders nothing of its own. Drop it anywhere in `App.vue`; it wraps its slot untouched so it
 * can also be used as a wrapper if a screen ever needs one.
 */

import { computed, onBeforeUnmount, watch } from 'vue';
import { brandPalette, type BrandPalette } from '@altamont/shared';

import { useConfigStore } from '@/stores/config';

const config = useConfigStore();

/**
 * The design tokens this client's colours stand in for.
 *
 * `--alt-navy` and `--alt-orange` are the two grounds the whole system is built on, and
 * `--alt-orange-600` is the accent's hover state. `--alt-on-navy` and `--alt-on-orange` are the
 * foregrounds that pair with them, published as their own properties because the stock palette
 * folds them into `--alt-navy` (the label on orange) and `--alt-white` (the label on navy) —
 * values that also serve as a ground and a card colour, so neither can be repointed at a
 * computed foreground without repainting something else. `--alt-navy-700` is the primary's hover
 * ground, kept in step so a secondary button does not flash stock navy under the cursor.
 */
function brandVariables(palette: BrandPalette): Record<string, string> {
  return {
    '--alt-navy': palette.primary,
    '--alt-navy-700': palette.primaryHover,
    '--alt-on-navy': palette.primaryText,
    '--alt-orange': palette.accent,
    '--alt-orange-600': palette.accentHover,
    '--alt-on-orange': palette.accentText,
  };
}

/**
 * This client's palette, or `null` while the config is still on its way. `brandPalette` is total:
 * a colour it cannot parse falls back to the design system's own, so a half-filled settings
 * document lands on stock Altamont rather than on something unreadable.
 */
const palette = computed<BrandPalette | null>(() =>
  config.loaded ? brandPalette(config.brandPrimaryColor, config.brandAccentColor) : null,
);

/** The element the custom properties go on, or `null` where there is no document at all. */
function root(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.documentElement;
}

/** Exactly the properties currently written by this component, so `restore` removes no others. */
let applied: readonly string[] = [];

function apply(next: BrandPalette): void {
  const element = root();
  if (!element) return;
  const variables = brandVariables(next);
  for (const [name, value] of Object.entries(variables)) {
    element.style.setProperty(name, value);
  }
  applied = Object.keys(variables);
}

function restore(): void {
  const element = root();
  if (element) {
    for (const name of applied) element.style.removeProperty(name);
  }
  applied = [];
}

// Immediate, so a remount with the config already in hand paints straight away instead of
// waiting for the next change and showing stock colours in between.
watch(
  palette,
  (next) => {
    if (next) apply(next);
    else restore();
  },
  { immediate: true },
);

onBeforeUnmount(restore);
</script>

<template>
  <slot />
</template>
